import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { Card, CardStatus } from "@feed/shared";
import { api } from "../api";
import { useToast } from "../components/Toast";
import type { CardList } from "./useCardList";

const MESSAGE: Record<CardStatus, string> = {
  unread: "toast.restored",
  read: "toast.read",
  saved: "toast.saved",
  dismissed: "toast.dismissed",
};

// Moves a card to another status the way every screen wants it: instantly on screen,
// rolled back with a message if the server says no, and (unless it's the automatic
// "opened, so read") a toast whose Undo puts the card back where it was.
export function useStatusChange(list: CardList, onChanged?: () => void) {
  const { t } = useTranslation();
  const toast = useToast();

  const apply = useCallback(
    async (card: Card, status: CardStatus): Promise<Card | null> => {
      list.patch({ ...card, status });
      try {
        const { card: saved } = await api.cards.setStatus(card.id, status);
        list.patch(saved);
        return saved;
      } catch {
        list.patch(card);
        toast.show(t("errors.save"));
        return null;
      }
    },
    [list, toast, t]
  );

  return useCallback(
    async (card: Card, status: CardStatus, options: { quiet?: boolean; message?: string } = {}) => {
      const previous = card.status;
      const saved = await apply(card, status);
      if (!saved) return;
      if (!options.quiet) {
        toast.show(options.message ?? t(MESSAGE[status]), () => {
          void apply(saved, previous).then(() => onChanged?.());
        });
      }
      onChanged?.();
    },
    [apply, toast, t, onChanged]
  );
}
